import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Skeleton, Tab, Tabs } from '@mui/material';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import { CustomerFormSubmissions } from '../../store/slices';
import { IStore } from '../../store/types';
import CustomerFormSubmissionsFilters, { DEFAULT_SUBMISSION_PARAMS } from '../../components/CustomerFormSubmissionsFilters';
import CustomerFormSubmissionsTable from '../../components/CustomerFormSubmissionsTable';
import FormSubmissionTypesTable from '../../components/FormSubmissionTypesTable';
import { normalizeFormSubmissionTypes } from '../../api/customerFormSubmissions';

/** `/customer-form-submissions`: forms sent from the shop, plus the catalogue of form types. */
const CustomerFormSubmissionsPage = () => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState(0);

    const { list, status, queryParams, types, typesStatus } = useSelector<IStore, any>(
        (state) => state.customerFormSubmissions || { list: [], status: null, queryParams: DEFAULT_SUBMISSION_PARAMS, types: [], typesStatus: null }
    );

    const loadSubmissions = (params: any) => {
        dispatch<any>(CustomerFormSubmissions.getCustomerFormSubmissions(params));
    };

    useEffect(() => {
        loadSubmissions({ ...DEFAULT_SUBMISSION_PARAMS, ...queryParams });
        dispatch<any>(CustomerFormSubmissions.getFormSubmissionTypes());
        // eslint-disable-next-line react-hooks/exhaustive-deps -- first load only
    }, []);

    const currentParams = { ...DEFAULT_SUBMISSION_PARAMS, ...queryParams };
    const typesList = normalizeFormSubmissionTypes(types);
    const typesMap = useMemo(() => {
        const map: Record<number, string> = {};
        typesList.forEach((type: any) => {
            map[type.id] = type.label;
        });
        return map;
    }, [typesList]);

    const rows: any[] = Array.isArray(list) ? list : [];
    const loading = status === 'loading';
    const failed = status === 'rejected';
    const total = Number(currentParams.total) || 0;

    let body: React.ReactNode;
    if (loading) {
        body = <Skeleton animation="wave" width="100%" height={480} variant="rectangular" />;
    } else if (failed) {
        body = <EmptyState title="The submissions could not be loaded" hint="Check the connection and try again." action={<Button variant="outlined" onClick={() => loadSubmissions(currentParams)}>Retry</Button>} />;
    } else if (rows.length === 0) {
        body = <EmptyState title="No submissions for these filters" hint="Widen the date range or reset the filters." />;
    } else {
        body = <CustomerFormSubmissionsTable submissions={rows} paging={currentParams} pagingUpdate={loadSubmissions} typesMap={typesMap} />;
    }

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Production"
                title="Form submissions"
                subtitle="Forms customers sent from the shop (wholesale requests and others); mark each one processed once it is handled."
                badges={activeTab === 0 && total > 0 ? <StatusChip tone="neutral">{total.toLocaleString('en-US')} total</StatusChip> : undefined}
            >
                <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} aria-label="Form submission sections">
                    <Tab label="Submissions" />
                    <Tab label="Form types" />
                </Tabs>
            </PageHeader>

            {activeTab === 0 && (
                <Panel padding="none">
                    <CustomerFormSubmissionsFilters queryParams={currentParams} loadSubmissions={loadSubmissions} loading={loading} types={typesList} />
                    {body}
                </Panel>
            )}

            {activeTab === 1 && (
                typesStatus === 'loading' && typesList.length === 0
                    ? <Skeleton animation="wave" width="100%" height={300} variant="rounded" />
                    : <FormSubmissionTypesTable types={typesList} loading={typesStatus === 'loading'} />
            )}
        </PageContainer>
    );
};

export default CustomerFormSubmissionsPage;
